import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import {
	MapPin,
	ArrowLeft,
	Save,
	Pencil,
	Play,
	Square,
	Loader2,
	SkipForward,
	Heart,
} from 'lucide-react'
import {
	useNavigate,
	useSearchParams,
	useBlocker,
	useLocation,
} from 'react-router-dom'

import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import {
	useCreateSchedule,
	useUpdateSchedule,
	useToggleScheduleFavorite,
} from '@/core/hooks/useSchedule'
import { useTranslation } from '@/core/i18n/useTranslation'
import { track } from '@/shared/analytics/analyticsService'
import { Badge } from '@/shared/components/atoms/Badge/Badge'
import { Button } from '@/shared/components/atoms/Button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'
import { useNotificationContext } from '@/shared/context/NotificationProvider'

import { RemoveScheduleModal } from './components/RemoveScheduleModal'
import { CalendarEvent } from './context/Data'
import { useEvents } from './context/EventContext'
import { convertEventsToSlots } from './utils/eventConverter'
import { detectCollision } from './utils/priorityCalculator'

interface SaveChangesButtonProps {
	initialEvents?: CalendarEvent[]
	scheduleId?: string
	scheduleTitle?: string
	scheduleDescription?: string
	scheduleTitleTranslations?: Record<string, string>
	scheduleDescriptionTranslations?: Record<string, string>
	refetchSchedule?: () => void
	onSaveSuccess?: () => void
	isPublic?: boolean
	contentCategoryIds?: number[]
}

export const SaveChangesButton: React.FC<SaveChangesButtonProps> = ({
	initialEvents = [],
	scheduleId,
	scheduleTitle,
	scheduleDescription,
	scheduleTitleTranslations,
	scheduleDescriptionTranslations,
	refetchSchedule,
	onSaveSuccess,
	isPublic = false,
	contentCategoryIds = [],
}) => {
	const { t } = useTranslation('common')
	const { events } = useEvents()
	const navigate = useNavigate()
	const location = useLocation()
	const [searchParams] = useSearchParams()
	const { showNotification } = useNotificationContext()
	const [isSaving, setIsSaving] = useState(false)
	const [publicEditWarningOpen, setPublicEditWarningOpen] = useState(false)

	const isCreationMode =
		searchParams.has('mode') && searchParams.get('mode') === 'create'
	const scheduleName = searchParams.get('name') || scheduleTitle || ''

	const createScheduleMutation = useCreateSchedule({
		config: useAccountConfig({}),
		options: {
			onSuccess: (data: any) => {
				if (onSaveSuccess) onSaveSuccess()
				showNotification({
					message: t(
						'entities.schedule.notifications.created_successfully_simple'
					),
					type: 'success',
				})
				// Preserve where we came from so the details page can decide where "Back" goes.
				// This is needed because `window.location.pathname` is always the *current* route.
				navigate(`/schedules/${data.id}`, {
					state: { from: `${location.pathname}${location.search}` },
				})
			},
			onError: (error: any) => {
				showNotification({
					message:
						error?.response?.data?.message ||
						t('entities.schedule.notifications.failed_to_create'),
					type: 'error',
				})
				setIsSaving(false)
			},
		},
	})

	const updateScheduleMutation = useUpdateSchedule({
		config: useAccountConfig({}),
		options: {
			onSuccess: async () => {
				showNotification({
					message: t('entities.schedule.notifications.updated_successfully'),
					type: 'success',
				})
				setIsSaving(false)
				// Refetch the schedule details to update initialEvents
				// IMPORTANT: Wait for refetch to complete before calling onSaveSuccess
				// to avoid race condition with stale cached data
				if (refetchSchedule) {
					await refetchSchedule()
				}
				if (onSaveSuccess) onSaveSuccess()
			},
			onError: (error: any) => {
				showNotification({
					message:
						error?.response?.data?.message ||
						t('entities.schedule.notifications.failed_to_update'),
					type: 'error',
				})
				setIsSaving(false)
			},
		},
	})

	const doSaveChanges = async () => {
		if (events.length === 0) {
			showNotification({
				message: t('entities.schedule.add_at_least_one_slot'),
				type: 'error',
			})
			return
		}

		// Check for overlapping timeslots before saving
		const collisions: { event1: string; event2: string }[] = []
		for (let i = 0; i < events.length; i++) {
			for (let j = i + 1; j < events.length; j++) {
				if (detectCollision(events[i], events[j])) {
					collisions.push({
						event1:
							events[i].title ||
							t('entities.schedule.timeslot_n', { n: i + 1 }),
						event2:
							events[j].title ||
							t('entities.schedule.timeslot_n', { n: j + 1 }),
					})
				}
			}
		}

		if (collisions.length > 0) {
			const collisionMessages = collisions
				.slice(0, 3) // Show max 3 collisions to avoid overwhelming the user
				.map((c) => `"${c.event1}" ↔ "${c.event2}"`)
				.join(', ')
			const moreMessage =
				collisions.length > 3
					? t('entities.schedule.more_collisions', {
							count: collisions.length - 3,
						})
					: ''
			showNotification({
				message: t('entities.schedule.overlapping_timeslots_detected', {
					collisions: collisionMessages,
					more: moreMessage,
				}),
				type: 'error',
			})
			return
		}

		// Validate that all events have either playlists (regular slots) or timeslotContents (smart slots)
		const invalidEvents: string[] = []
		const validEvents = events.filter((event: CalendarEvent) => {
			// Check if it's a smart slot with timeslotContents
			if (event.timeslotContents && event.timeslotContents.length > 0) {
				// Validate that each condition has playlists
				const isValid = event.timeslotContents.every(
					(content) => content.playlists && content.playlists.length > 0
				)
				if (!isValid) {
					const missingConditions = event.timeslotContents
						.filter(
							(content) => !content.playlists || content.playlists.length === 0
						)
						.map((content) => content.type)
					invalidEvents.push(`${event.title}: ${missingConditions.join(', ')}`)
				}
				return isValid
			}
			// Otherwise it's a regular slot, check for direct playlists
			const isValid = event.playlists && event.playlists.length > 0
			if (!isValid) {
				invalidEvents.push(event.title)
			}
			return isValid
		})

		if (validEvents.length !== events.length) {
			const errorMessage =
				invalidEvents.length > 0
					? t('entities.schedule.missing_playlists_in', {
							slots: invalidEvents.join(', '),
						})
					: t('entities.schedule.all_slots_must_have_playlists')
			showNotification({
				message: errorMessage,
				type: 'error',
			})
			return
		}

		setIsSaving(true)

		const slots = convertEventsToSlots(events as any)
		const sanitizedSlotsForCreate = slots.map((slot) => ({
			name: slot.name,
			type: slot.type,
			priority: slot.priority,
			vevent: slot.vevent,
			timeslot_contents: slot.timeslot_contents.map((content) => ({
				type: content.type,
				volume: content.volume,
				...(content.min_percentage !== undefined
					? { min_percentage: content.min_percentage }
					: {}),
				...(content.max_percentage !== undefined
					? { max_percentage: content.max_percentage }
					: {}),
				playlists: content.playlists,
			})),
		}))

		// Use translation objects if available, otherwise fallback to single title/description for backward compatibility
		const titleObj: Record<string, string> = scheduleTitleTranslations || {
			'en-US': scheduleName,
		}

		const descriptionObj: Record<string, string> =
			scheduleDescriptionTranslations || {
				'en-US': scheduleDescription || '',
			}

		const payload = {
			title: titleObj,
			description: descriptionObj,
			timeslots: isCreationMode ? sanitizedSlotsForCreate : slots,
			public: isPublic,
			content_category_ids: contentCategoryIds,
		}

		if (isCreationMode) {
			createScheduleMutation.mutate(payload)
		} else {
			updateScheduleMutation.mutate({
				scheduleId: scheduleId!,
				data: payload,
			})
		}
	}

	const handleSaveClick = () => {
		// For public schedules, require explicit confirmation before saving edits.
		if (!isCreationMode && isPublic) {
			setPublicEditWarningOpen(true)
			return
		}

		doSaveChanges()
	}

	const hasChanges = () => {
		if (isCreationMode) {
			return events.length > 0
		}
		// In edit mode, compare current events with initial events
		return JSON.stringify(events) !== JSON.stringify(initialEvents)
	}

	const hasUnsavedChanges = hasChanges()

	return (
		<>
			<Dialog
				open={publicEditWarningOpen}
				onOpenChange={setPublicEditWarningOpen}
			>
				<DialogContent className="sm:max-w-[520px]">
					<DialogHeader>
						<DialogTitle>{t('schedules.publicEditWarning.title')}</DialogTitle>
						<DialogDescription>
							{t('schedules.publicEditWarning.description')}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setPublicEditWarningOpen(false)}
						>
							{t('actions.cancel')}
						</Button>
						<Button
							onClick={() => {
								setPublicEditWarningOpen(false)
								doSaveChanges()
							}}
						>
							{t('actions.continue')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Button
				onClick={handleSaveClick}
				disabled={!hasUnsavedChanges || isSaving}
				className="rounded-full"
				variant={isCreationMode ? 'default' : 'outline'}
			>
				<Save className="mr-2 h-4 w-4" />
				{isSaving
					? t('entities.schedule.saving')
					: t('entities.schedule.save_changes')}
			</Button>
		</>
	)
}

interface ScheduleDetailsHeaderProps {
	scheduleName: string
	isCreationMode: boolean
	onRequestEdit: () => void
	initialEvents: CalendarEvent[]
	scheduleId?: string
	scheduleDescription: string
	scheduleTitleTranslations?: Record<string, string>
	scheduleDescriptionTranslations?: Record<string, string>
	refetchSchedule: () => void
	onOpenAssignModal: () => void
	isPublic?: boolean
	contentCategoryIds?: number[]
	isReadOnly?: boolean
	isFavorite?: boolean
	onPreviewPlay?: () => void
	onPreviewSkip?: () => void
	isPreviewPlaying?: boolean
	isPreviewLoading?: boolean
	isPreviewStarting?: boolean
	isPreviewDisabled?: boolean
	isPreviewSkipDisabled?: boolean
}

export const ScheduleDetailsHeader: React.FC<ScheduleDetailsHeaderProps> = ({
	scheduleName,
	isCreationMode,
	onRequestEdit,
	initialEvents,
	scheduleId,
	scheduleDescription,
	scheduleTitleTranslations,
	scheduleDescriptionTranslations,
	refetchSchedule,
	onOpenAssignModal,
	isPublic = false,
	contentCategoryIds = [],
	isReadOnly = false,
	isFavorite = false,
	onPreviewPlay,
	onPreviewSkip,
	isPreviewPlaying = false,
	isPreviewLoading = false,
	isPreviewStarting = false,
	isPreviewDisabled = false,
	isPreviewSkipDisabled = false,
}) => {
	const { t } = useTranslation('common')
	const navigate = useNavigate()
	const location = useLocation()
	const queryClient = useQueryClient()
	const { showNotification } = useNotificationContext()
	const { events, setEvents } = useEvents()
	const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false)
	const [savedState, setSavedState] = useState<string | null>(null)
	const isWaitingForSync = useRef(false)
	const isPerformingControlledNavigation = useRef(false)
	const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false)
	const [optimisticFavorite, setOptimisticFavorite] = useState(isFavorite)
	const [isFavoriteLoading, setIsFavoriteLoading] = useState(false)

	const { mutateAsync: toggleFavorite } = useToggleScheduleFavorite({
		config: useAccountConfig({}),
		options: {
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: ['schedules-assigned'] })
				queryClient.invalidateQueries({ queryKey: ['schedules'] })
				queryClient.invalidateQueries({ queryKey: ['schedules-favorites'] })
				if (scheduleId) {
					queryClient.invalidateQueries({
						queryKey: ['schedule-details', scheduleId],
					})
				}
			},
		},
	})

	useEffect(() => {
		setOptimisticFavorite(isFavorite)
	}, [isFavorite])

	const hasChanges = useMemo(() => {
		const currentEventsStr = JSON.stringify(events)
		if (savedState) {
			return currentEventsStr !== savedState
		}
		if (isCreationMode) {
			return events.length > 0
		}
		return currentEventsStr !== JSON.stringify(initialEvents)
	}, [events, initialEvents, isCreationMode, savedState])

	useEffect(() => {
		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			if (hasChanges) {
				e.preventDefault()
				e.returnValue = ''
			}
		}
		window.addEventListener('beforeunload', handleBeforeUnload)
		return () => window.removeEventListener('beforeunload', handleBeforeUnload)
	}, [hasChanges])

	const blocker = useBlocker(
		({ currentLocation, nextLocation }) =>
			hasChanges &&
			currentLocation.pathname !== nextLocation.pathname &&
			!isPerformingControlledNavigation.current
	)

	useEffect(() => {
		setSavedState(null)
		isPerformingControlledNavigation.current = false
	}, [scheduleId])

	// Handle navigation blocking when there are unsaved changes.
	// If the blocker state is 'blocked', it prompts the user for confirmation.
	// If confirmed, it proceeds with navigation; otherwise, it resets the blocker.
	useEffect(() => {
		if (blocker.state === 'blocked') {
			const confirm = window.confirm(
				t('entities.schedule.unsaved_changes_warning')
			)
			if (confirm) {
				blocker.proceed()
			} else {
				blocker.reset()
			}
		}
	}, [blocker, t])

	// Synchronize local events with server data after a successful save.
	// This is crucial for replacing temporary IDs (e.g., from new slots) with real server IDs.
	// It runs when 'initialEvents' updates (after refetch) and we are waiting for a sync.
	useEffect(() => {
		if (isWaitingForSync.current && initialEvents) {
			setEvents(initialEvents)
			setSavedState(null)
			isWaitingForSync.current = false
		}
	}, [initialEvents, setEvents])

	const handleBack = () => {
		const from = (location.state as any)?.from
		const cameFromNew =
			typeof from === 'string' && from.includes('/schedules/new')

		// If we are currently on /schedules/new, OR we arrived here from /schedules/new,
		// go back to schedules list instead of going back to the creation flow.
		if (location.pathname.includes('/schedules/new') || cameFromNew) {
			navigate(`/schedules`)
			return
		}

		// Otherwise, navigate to the previous page
		navigate(-1)
	}

	const isDescriptionLong = scheduleDescription.length > 180
	const displayDescription =
		isDescriptionExpanded || !isDescriptionLong
			? scheduleDescription
			: scheduleDescription.substring(0, 180) + '...'
	const handlePreviewPlayClick = () => {
		track('schedule_preview_playback_button')
		onPreviewPlay?.()
	}

	const handleFavoriteClick = useCallback(async () => {
		if (!scheduleId || isFavoriteLoading) return

		const previousFavorite = optimisticFavorite
		const nextFavorite = !previousFavorite
		setOptimisticFavorite(nextFavorite)
		setIsFavoriteLoading(true)

		try {
			await toggleFavorite({
				scheduleId,
				favorite: nextFavorite,
			})
		} catch {
			setOptimisticFavorite(previousFavorite)
			showNotification({
				message: 'Failed to update favorites',
				type: 'error',
			})
		} finally {
			setIsFavoriteLoading(false)
		}
	}, [
		scheduleId,
		isFavoriteLoading,
		optimisticFavorite,
		toggleFavorite,
		showNotification,
	])

	return (
		<div className="lg:space-y-3 space-y-4">
			<div className="hidden md:flex items-center justify-between gap-4">
				<div className="flex items-center gap-3 flex-1 min-w-0">
					<button
						onClick={handleBack}
						className="flex items-center justify-center hover:opacity-70 transition-opacity cursor-pointer"
					>
						<ArrowLeft className="h-5 w-5" />
					</button>
					<div className="flex items-start gap-2 text-2xl font-semibold tracking-tight md:text-3xl min-w-0">
						<h2 className="truncate">{scheduleName}</h2>
						{isCreationMode && (
							<Badge variant="secondary" className="text-xs">
								{t('entities.schedule.draft')}
							</Badge>
						)}
						{!isCreationMode && !isReadOnly && (
							<button
								onClick={onRequestEdit}
								className="cursor-pointer transition-colors m-auto"
								title={t('entities.schedule.edit_schedule')}
							>
								<Pencil className="h-5 w-5 text-muted-foreground hover:text-foreground" />
							</button>
						)}
					</div>
				</div>
				<div className="flex items-center gap-2">
					{!isCreationMode && onPreviewPlay && (
						<div className="flex items-center gap-2">
							<Button
								variant="default"
								className="rounded-full w-fit md:inline-flex"
								onClick={handlePreviewPlayClick}
								disabled={isPreviewDisabled || isPreviewLoading}
								title={
									isPreviewPlaying
										? t('entities.schedule.card.preview_stop', 'Stop preview')
										: t('entities.schedule.card.preview_play', 'Play preview')
								}
							>
								{isPreviewLoading || (isPreviewPlaying && isPreviewStarting) ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : isPreviewPlaying ? (
									<Square className="h-4 w-4 fill-current" />
								) : (
									<Play className="h-4 w-4 fill-current" />
								)}
								{isPreviewPlaying
									? isPreviewStarting
										? t('entities.schedule.card.preview_starting', 'Starting…')
										: t('entities.schedule.card.preview_playing', 'Playing')
									: t('entities.schedule.card.preview_play', 'Play preview')}
							</Button>

							{isPreviewPlaying && onPreviewSkip && (
								<Button
									variant="outline"
									className="rounded-full"
									onClick={onPreviewSkip}
									disabled={isPreviewSkipDisabled}
									title={t('entities.schedule.card.preview_skip', 'Skip')}
								>
									<SkipForward className="h-4 w-4" />
								</Button>
							)}
						</div>
					)}
					{/* Heart icon to favorite the schedule */}
					<Button
						variant="ghost"
						className="rounded-full"
						onClick={handleFavoriteClick}
						disabled={!scheduleId || isFavoriteLoading}
						title={t('entities.schedule.card.favorite', 'Favorite')}
					>
						<Heart
							className={`h-4 w-4 ${optimisticFavorite ? 'fill-white text-white' : ''}`}
						/>
					</Button>
					{!isCreationMode && (
						<Button
							variant="outline"
							className="rounded-full cursor-pointer"
							onClick={onOpenAssignModal}
							id="schedule-details-header-manage-assignment-button"
							data-testid="schedule-details-header-manage-assignment-button"
						>
							{t('entities.schedule.manage_assignment')}
							<MapPin className="!h-4 !w-4" />
						</Button>
					)}
					{!isReadOnly && hasChanges && (
						<SaveChangesButton
							initialEvents={initialEvents}
							scheduleId={scheduleId}
							scheduleTitle={scheduleName}
							scheduleDescription={scheduleDescription}
							scheduleTitleTranslations={scheduleTitleTranslations}
							scheduleDescriptionTranslations={scheduleDescriptionTranslations}
							refetchSchedule={refetchSchedule}
							isPublic={isPublic}
							contentCategoryIds={contentCategoryIds}
							onSaveSuccess={() => {
								isPerformingControlledNavigation.current = true
								setSavedState(JSON.stringify(events))
								isWaitingForSync.current = true
							}}
						/>
					)}
				</div>
			</div>
			<div className="md:hidden flex items-center justify-between border-b border-border py-3 px-5">
				<div className="flex items-center gap-2 min-w-0">
					<button
						type="button"
						onClick={handleBack}
						className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-muted/80 transition-colors shrink-0"
					>
						<ArrowLeft className="w-5 h-5" />
					</button>
					<h2 className="text-lg font-semibold tracking-tight truncate">
						{scheduleName}
					</h2>
				</div>
				{!isCreationMode && !isReadOnly && (
					<div className="flex items-center gap-2 shrink-0">
						<button
							type="button"
							onClick={onRequestEdit}
							className="h-9 w-9 flex items-center justify-center bg-outline border border-border rounded-full hover:bg-muted/80 transition-colors"
							title={t('entities.schedule.edit_schedule')}
						>
							<Pencil className="h-4 w-4 text-muted-foreground" />
						</button>
					</div>
				)}
			</div>
			{/* Schedule description */}
			<div className="md:hidden w-full space-y-2 px-2">
				<p className=" text-xs md:text-sm text-muted-foreground w-full">
					{displayDescription}
				</p>
				{isDescriptionLong && (
					<button
						onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
						className="text-xs md:text-sm text-primary hover:underline font-semibold"
					>
						{isDescriptionExpanded
							? t('entities.schedule.see_less')
							: t('entities.schedule.see_more')}
					</button>
				)}
			</div>

			<div className="md:hidden flex items-center gap-2 px-2 w-full">
				{!isCreationMode && onPreviewPlay && (
					<>
						<Button
							variant="default"
							className="hidden md:inline-flex rounded-full"
							onClick={handlePreviewPlayClick}
							disabled={isPreviewDisabled || isPreviewLoading}
						>
							{isPreviewLoading || (isPreviewPlaying && isPreviewStarting) ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : isPreviewPlaying ? (
								<Square className="h-4 w-4 fill-current" />
							) : (
								<Play className="h-4 w-4 fill-current" />
							)}
						</Button>
						{isPreviewPlaying && onPreviewSkip && (
							<Button
								variant="outline"
								className="rounded-full"
								onClick={onPreviewSkip}
								disabled={isPreviewSkipDisabled}
							>
								<SkipForward className="h-4 w-4" />
							</Button>
						)}
					</>
				)}
				{!isCreationMode && (
					<>
						<Button
							variant="outline"
							className="w-full rounded-full cursor-pointer"
							onClick={onOpenAssignModal}
							id="schedule-details-header-manage-assignment-button-mobile"
							data-testid="schedule-details-header-manage-assignment-button-mobile"
						>
							{t('entities.schedule.manage_assignment')}
							<MapPin className="!h-4 !w-4" />
						</Button>
						<div className="flex items-center gap-2">
							<Button
								variant="ghost"
								className="h-11 w-11 rounded-full border border-border bg-muted/30 hover:bg-muted/60 p-0"
								onClick={handleFavoriteClick}
								disabled={!scheduleId || isFavoriteLoading}
								title={t('entities.schedule.card.favorite', 'Favorite')}
							>
								<Heart
									className={`h-4 w-4 ${optimisticFavorite ? 'fill-white text-white' : ''}`}
								/>
							</Button>
						</div>
					</>
				)}
				{!isReadOnly && hasChanges && (
					<SaveChangesButton
						initialEvents={initialEvents}
						scheduleId={scheduleId}
						scheduleTitle={scheduleName}
						scheduleDescription={scheduleDescription}
						scheduleTitleTranslations={scheduleTitleTranslations}
						scheduleDescriptionTranslations={scheduleDescriptionTranslations}
						refetchSchedule={refetchSchedule}
						isPublic={isPublic}
						contentCategoryIds={contentCategoryIds}
						onSaveSuccess={() => {
							isPerformingControlledNavigation.current = true
							setSavedState(JSON.stringify(events))
							isWaitingForSync.current = true
						}}
					/>
				)}
			</div>
			<div className="hidden md:block w-full space-y-2">
				<p className="text-xs md:text-sm font-medium text-muted-foreground w-full">
					{displayDescription}
				</p>
				{isDescriptionLong && (
					<button
						onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
						className="text-xs md:text-sm text-primary hover:underline font-semibold"
					>
						{isDescriptionExpanded
							? t('entities.schedule.see_less')
							: t('entities.schedule.see_more')}
					</button>
				)}
			</div>
			<RemoveScheduleModal
				open={isRemoveModalOpen}
				onOpenChange={setIsRemoveModalOpen}
				scheduleId={scheduleId}
				scheduleName={scheduleName}
				scheduleDescription={scheduleDescription}
				onRemoved={handleBack}
			/>
		</div>
	)
}
